import { differenceInMilliseconds } from 'date-fns/differenceInMilliseconds'
import { isPast } from 'date-fns/isPast'
import { parseISO } from 'date-fns/parseISO'
import { storeToRefs } from 'pinia'
import { onBeforeMount, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'

import { useAuthActions } from '@/auth/useAuthActions'
import { useAuthStore } from '@/auth/useAuthStore'
import { logError } from '@/logger'
import { useEmitter as messageListener } from '@/utils/useEmitter'

// 2147483647 is the max setTimeout value (32 bit integer value)
const TIMEOUT_MAX = 2147483647

export const useSessionManager = (): void => {
  let timeout: number | null = null

  const { logout } = useAuthActions()
  const router = useRouter()

  const authStore = useAuthStore()
  const { expiresAt, isAuthenticated } = storeToRefs(authStore)

  // Guards against the concurrent brand-scoped 403s that fan out on a stale
  // tab (e.g. plans + capabilities + domains all denied at once) each kicking
  // off their own recovery + redirect.
  let recoveringBrandAccess = false

  const onBrandAccessLost = async (): Promise<void> => {
    if (recoveringBrandAccess) return
    recoveringBrandAccess = true
    try {
      const recovered = await authStore.ensureAccessibleBrandSession()
      if (recovered) {
        await router.replace(authStore.home)
      }
    } catch (error) {
      // The emitter doesn't await this handler, so a rejected redirect
      // (e.g. a navigation guard aborting) would escape unobserved.
      logError(error, { context: 'onBrandAccessLost' })
    } finally {
      recoveringBrandAccess = false
    }
  }

  const onTimoutTriggered = async (): Promise<void> => {
    if (expiresAt.value && isPast(expiresAt.value)) {
      await logout()
    } else {
      registerTimeout()
    }
  }

  const registerTimeout = (): void => {
    if (timeout) window.clearTimeout(timeout)

    if (expiresAt.value) {
      const timeoutDuration = differenceInMilliseconds(
        parseISO(expiresAt.value),
        new Date()
      )

      timeout = window.setTimeout(
        onTimoutTriggered,

        Math.min(timeoutDuration, TIMEOUT_MAX)
      )
    }
  }

  watch(isAuthenticated, async (isAuthed) => {
    if (isAuthed) {
      registerTimeout()
    } else {
      await logout()
    }
  })

  onBeforeMount(async () => {
    if (isAuthenticated.value) {
      registerTimeout()
    }

    messageListener.on('auth.logout', logout)
    messageListener.on('auth.brand-access-lost', onBrandAccessLost)
  })

  onBeforeUnmount(() => {
    if (timeout) window.clearTimeout(timeout)
    messageListener.off('auth.logout', logout)
    messageListener.off('auth.brand-access-lost', onBrandAccessLost)
  })
}
